/**
 * dataset 取得系
 */

// React のマウント要素
const getAppRoot = () => document.querySelector(".react-app-router");

// dataset から値を取得
const readDataset = (key) => getAppRoot()?.dataset?.[key] ?? null;

// JSON安全パース
const safeJsonParse = (raw) => {
  if (!raw) return null; // 空や未定義は null
  try {
    return JSON.parse(raw);
  } catch {
    return null; // JSONでなければ null
  }
};

// ログインユーザー（current_login_user）
export const getLoginUserFromDataset = () =>
  safeJsonParse(readDataset("loginUser"));

// Userログイン時の current_user
export const getCurrentUserFromDataset = () =>
  safeJsonParse(readDataset("currentUser"));

// ログインユーザーの型（"User" / "CompanyUser"）
export const getLoginUserTypeFromDataset = () => readDataset("loginUserType");

// 他人編集が可能か
export const canEditUser = () =>
  safeJsonParse(readDataset("canEditUser")) === true;

/**
 * ユーザー判定系
 */

// ユーザーの型（"User" / "CompanyUser"）
const getUserType = (obj) => obj?.type ?? null;

// id を文字列で統一
const getUserId = (obj) => (obj?.id != null ? String(obj.id) : null);

// 同一ユーザー判定
const isSameUser = (a, b) => {
  if (!getUserId(a) || !getUserId(b)) return false;

  const aType = getUserType(a);
  const bType = getUserType(b);

  // 型が取れる場合は型＋idで比較
  if (aType && bType) {
    return aType === bType && getUserId(a) === getUserId(b);
  }

  // 型が取れない場合は id のみ
  return getUserId(a) === getUserId(b);
};

// 他ユーザーを編集しているか？
export const otherUserEditMode = (
  target,
  loginUser = getLoginUserFromDataset(),
) => {
  if (!canEditUser()) return false; // 権限なし
  if (!getUserId(target)) return false; // 対象なし
  if (!getUserId(loginUser)) return false; // ログイン情報なし

  // loginUserType は dataset を優先
  const loginType = getLoginUserTypeFromDataset() || getUserType(loginUser);
  const targetType = getUserType(target);

  // 型が取れない場合は id 比較のみ
  if (!loginType || !targetType) {
    return !isSameUser(target, loginUser);
  }

  // 型が違えば別ユーザー
  if (loginType !== targetType) return true;

  // 同じ型なら id で比較
  return !isSameUser(target, loginUser);
};

/**
 * URL 組み立て
 */

// 編集対象に応じたパラメータ名
const paramNameForTarget = (target) =>
  getUserType(target) === "User" ? "user_id" : "company_user_id";

// 他人編集時のみ user_id / company_user_id を付与
export const withUserParam = (
  basePath,
  target,
  loginUser = getLoginUserFromDataset(),
) => {
  if (!basePath) return basePath; // 空URL防止
  if (!otherUserEditMode(target, loginUser)) return basePath;

  const id = getUserId(target);
  if (!id) return basePath;

  const url = new URL(basePath, window.location.origin);
  url.searchParams.set(paramNameForTarget(target), id);

  return `${url.pathname}${url.search}`;
};

// owner に応じたベースパス
export const resolveOwnerBasePath = (owner) => {
  if (getUserType(owner) === "CompanyUser" && getUserId(owner)) {
    return `/company/members/${getUserId(owner)}`;
  }
  return "";
};

/**
 * 各画面のパス生成
 */

export const editSkillSheetBasicsPathFor = (owner) =>
  withUserParam(
    `${resolveOwnerBasePath(owner)}/skill_sheet_basics/edit`,
    owner,
  );

export const newSkillSheetPathFor = (owner) =>
  withUserParam(`${resolveOwnerBasePath(owner)}/skill_sheet_mains/new`, owner);

export const editSkillSheetPathFor = (workHistoryId, owner) =>
  withUserParam(
    `${resolveOwnerBasePath(owner)}/skill_sheet_mains/${workHistoryId}/edit`,
    owner,
  );

export const deleteSkillSheetPathFor = (workHistoryId, owner) =>
  withUserParam(
    `${resolveOwnerBasePath(owner)}/skill_sheet_mains/${workHistoryId}`,
    owner,
  );

export const editSkillSheetSelfEvaluationsPathFor = (owner) =>
  withUserParam(
    `${resolveOwnerBasePath(owner)}/skill_sheet_self_evaluations/edit`,
    owner,
  );

export const indexSkillSheetMainsPathFor = (owner) =>
  withUserParam(`${resolveOwnerBasePath(owner)}/skill_sheet_mains`, owner);

// 保存後の戻り先
export const memberSkillSheetPathFor = (owner) => {
  const userType = getUserType(owner);
  const id = getUserId(owner);

  // 自分なら一覧へ
  if (!otherUserEditMode(owner)) {
    return "/skill_sheets";
  }

  // 他人なら対象ページへ
  if (id && userType === "CompanyUser")
    return `/company/members/${id}/skill_sheet`;
  if (id && userType === "User") return `/user_show/${id}`;
  return "/skill_sheets";
};

/**
 * target 生成
 */

// id から target を生成
export const targetFromIds = ({ userId, companyUserId } = {}) => {
  if (userId) return { id: Number(userId), type: "User" };
  if (companyUserId) return { id: Number(companyUserId), type: "CompanyUser" };
  return null;
};

// Data から target を生成
export const targetFromFormData = (formData) =>
  targetFromIds({
    userId: formData?.user_id,
    companyUserId: formData?.company_user_id,
  });

// Context から target を生成
export const targetFromContext = ({ targetUserId, targetUserType } = {}) =>
  targetFromIds({
    userId:
      targetUserType === "user" || targetUserType === "User"
        ? targetUserId
        : null,
    companyUserId:
      targetUserType === "companyuser" || targetUserType === "CompanyUser"
        ? targetUserId
        : null,
  });
